import {
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
    getApiObjectCoordinates,
    isApartment,
} from '@bpd-library/utilities';

/**
 * @description Checks if an object of the following types should be drawn on the map.
 * @export
 * @param {(ExtendedConstructionNumber | ExtendedPropertyType | ExtendedProjectPhase)} object
 * @return {boolean}
 */
export default function shouldDrawPolygon(
    object: ExtendedConstructionNumber | ExtendedPropertyType | ExtendedProjectPhase,
    options?: Record<string, any>,
): boolean {
    switch (object.objectType) {
        case 'constructionNumber':
            return shouldDrawConstructionNumber(object);
        case 'propertyType':
            return shouldDrawPropertyType(object);
        case 'projectPhase':
            return shouldDrawProjectPhase(object, options);
    }
}

function shouldDrawConstructionNumber(constructionNumber: ExtendedConstructionNumber): boolean {
    const isApartmentBuilding = isApartment({ constructionNumber });
    const hasCoordinates = !!getApiObjectCoordinates(constructionNumber);

    return !isApartmentBuilding && hasCoordinates;
}

function shouldDrawPropertyType(propertyType: ExtendedPropertyType): boolean {
    const hasApartmentBuilding = !!propertyType.apartmentBuildings?.length;
    const constructionNumberIsDrawn = !!propertyType.constructionNumbers?.find(
        shouldDrawConstructionNumber,
    );
    const hasCoordinates = !!getApiObjectCoordinates(propertyType);

    return !constructionNumberIsDrawn && hasCoordinates && !hasApartmentBuilding;
}

function shouldDrawProjectPhase(
    projectPhase: ExtendedProjectPhase,
    options?: { alwaysShowSoldProjectPolygons?: boolean },
): boolean {
    const propertyTypeIsDrawn = !!projectPhase.propertyTypes?.find(shouldDrawPropertyType);
    const constructionNumberIsDrawn = !!projectPhase.constructionNumbers?.find(
        shouldDrawConstructionNumber,
    );
    const hasCoordinates = !!getApiObjectCoordinates(projectPhase);
    const showSold = !!options?.alwaysShowSoldProjectPolygons && projectPhase.status === 'Sold';

    return ((!propertyTypeIsDrawn && !constructionNumberIsDrawn) || showSold) && hasCoordinates;
}
